/**
 * Каталог сообщений проверки пакета розыгрыша (по образцу packages/protocol/src/messages.ts).
 *
 * Каждая проверка (Check) несёт и готовый русский текст (title, details — консольная утилита),
 * и коды с параметрами (notes) — клиент переводит их своим словарём (i18n/<язык>/verify.ts,
 * ключи 'check.<код>'). Русский текст строится из этого же каталога.
 *
 * Шаблоны:
 *   {x}            — параметр как есть (строка, число без форматирования);
 *   {x|cr}         — сумма в минорных единицах → кредиты (массив сумм — через « / »);
 *   {x|num}        — число с двумя знаками после запятой по правилам языка;
 *   {n:ф1|ф2|ф3}   — форма слова для числа n в порядке категорий множественного числа языка
 *                    (ru: one|few|many, en/de: one|other);
 *   вложенная заметка ({ code, params }) переводится тем же каталогом, массив заметок — через «; »;
 *   { srv: Msg } — сообщение сервера (причина отмены), переводится каталогом @mt/protocol.
 */
import { msgRu, type Msg } from '@mt/protocol';

export const VERIFY_MESSAGES_RU = {
  // ── заголовки проверок (title.<id проверки>) ──
  'title.format': 'Формат файла',
  'title.signature': 'Подпись сервера (Ed25519)',
  'title.commitment': 'Обязательство: состав зафиксирован до ставок',
  'title.aggregate': 'Бары агрегата пересчитаны из котировок акций',
  'title.prices': 'P₀, P₁ и изменение Δ',
  'title.timeline': 'Хронология: ходы до фиксации, раскрытия по этапам',
  'title.contributions': 'Вклады игроков совпадают с проводками',
  'title.settlement': 'Расчёт выплат пересчитан заново',
  'title.ledger': 'Проводки кредитов сходятся',
  // ── итог отчёта ──
  'summary.unrecognized': 'файл не распознан',
  'summary.ok': 'Все проверки пройдены ({ok} из {total}).',
  'summary.okWarn': 'Все проверки пройдены ({ok} из {total}, есть замечания).',
  'summary.fail': 'Не пройдено: {titles}.',
  // ── формат ──
  'format.notPackage': 'это не пакет проверки «Рыночного стола»',
  // ── подпись ──
  'signature.missing': 'подпись отсутствует или неизвестный алгоритм',
  'signature.keyMismatch': 'ключ в файле не совпадает с опубликованным ключом сервера',
  'signature.keyTrusted': 'ключ {keyId} совпадает с опубликованным сервером',
  'signature.keyUntrusted': 'ключ {keyId} (сверьте с /api/verification-key)',
  'signature.intact': 'содержимое пакета не изменялось после выдачи',
  'signature.invalid': 'подпись не соответствует содержимому — файл изменён',
  'signature.error': 'ошибка проверки подписи: {error}',
  // ── обязательство ──
  'commitment.hashOk': 'SHA-256(соль:параметры) = {hash}… совпадает с опубликованным хешем',
  'commitment.hashMismatch': 'хеш не совпадает: вычислено {computed}, опубликовано {published}',
  'commitment.badJson': 'параметры обязательства — некорректный JSON',
  'commitment.symbolsMismatch': 'состав в обязательстве {committed} ≠ раскрытому {revealed}',
  'commitment.symbols': 'состав в обязательстве: {symbols}',
  'commitment.basePricesMismatch': 'базовые цены в обязательстве не совпадают с раскрытыми',
  'commitment.basePricesOk': 'базовые цены совпадают',
  'commitment.p0Mismatch': 'время бара P₀ в обязательстве не совпадает',
  'commitment.formulaMismatch': 'формула/база/шаг в обязательстве не совпадают с применёнными',
  'commitment.sessionMismatch': 'идентификатор сессии в обязательстве не совпадает',
  // ── бары агрегата ──
  'aggregate.barMismatch': 'бар k={k} ({time}): {fields}',
  'aggregate.field': '{field}: показано {shown}, пересчитано {recomputed}',
  'aggregate.unknownFormula': 'формула {formula} неизвестна этой версии проверки или не подходит для {n} {n:компонента|компонентов|компонентов}',
  'aggregate.ok': '{n} {n:бар совпал|бара совпали|баров совпали} с пересчётом по формуле {formula}',
  'aggregate.source': 'источник котировок: {source}',
  // ── цены ──
  'prices.p0Mismatch': 'P₀ должен быть {base}: пересчитано {recomputed}, в расчёте {settled}',
  'prices.p0': 'P₀ = {p0|num} (закрытие бара {time})',
  'prices.noTarget': 'нет целевой свечи для пересчёта P₁',
  'prices.p1Mismatch': 'P₁: в расчёте {settled}, пересчитано {recomputed}',
  'prices.deltaMismatch': 'Δ в шагах: в расчёте {settled}, пересчитано {recomputed}',
  'prices.p1': 'P₁ = {p1|num} (закрытие целевой минуты {time}), Δ = {sign}{delta|num}',
  'prices.notNeeded': 'исход «{kind}» — цена P₁ для расчёта не требовалась',
  // ── хронология ──
  'timeline.late': 'ходы после фиксации: {actions}',
  'timeline.allBefore': 'все ходы ({n}) сделаны до фиксации прогнозов в {time}',
  'timeline.compositionEarly': 'состав раскрыт раньше начала круга 3',
  'timeline.compositionOk': 'состав раскрыт в {time} — с началом круга 3',
  'timeline.viewerLate': 'изменения вашего прогноза после фиксации: {n}',
  'timeline.viewerOk': 'ваш прогноз менялся {n} {n:раз|раза|раз}, все изменения — до фиксации',
  'timeline.finalMismatch': 'итоговый прогноз в расчёте отличается от последнего вашего изменения',
  'timeline.unordered': 'журнал ходов не упорядочен',
  // ── вклады ──
  'contributions.mismatch': '{name}: по проводкам взнос {ante|cr}, круги {rounds|cr}; в расчёте взнос {settledAnte|cr}, круги {settledRounds|cr}',
  'contributions.ok': 'у {n} {n:участника|участников|участников} взносы и ставки по кругам совпадают с проводками',
  // ── расчёт ──
  'settlement.error': 'не удалось пересчитать: {error}',
  'settlement.statusMismatch': 'статус: в пакете «{settled}», пересчитано «{recomputed}»',
  'settlement.noPlayer': 'нет игрока {playerId} в расчёте',
  'settlement.playerMismatch': '{name}: выплата {payout|cr} / возврат {refund|cr} в пакете, пересчитано {rePayout|cr} / {reRefund|cr}',
  'settlement.commissionMismatch': 'комиссия по кругам: в пакете {settled}, пересчитано {recomputed}',
  'settlement.bankMismatch': 'удержано банком: в пакете {settled|cr}, пересчитано {recomputed|cr}',
  'settlement.unbalanced': 'пересчитанный расчёт не сходится',
  'settlement.outcome': 'исход: {kind}',
  'settlement.outcomeReason': 'исход: {kind} ({reason})',
  'settlement.kind.market': 'по рынку',
  'settlement.kind.walkover': 'остался один участник',
  'settlement.kind.cancel': 'отмена',
  'settlement.totals': 'банков: {pots}; комиссия по кругам {commission|cr}; удержано {withheld|cr}',
  'settlement.payout': '{name}: выплата {payout|cr}',
  'settlement.payoutError': '{name}: выплата {payout|cr}, ошибка прогноза {error|num} п.',
  'settlement.wrongDirection': 'выплата игроку с неверным направлением',
  // ── проводки ──
  'ledger.escrowNonZero': 'остаток на счёте розыгрыша: {net|cr} (должен быть 0)',
  'ledger.escrowZero': 'счёт розыгрыша закрыт в ноль',
  'ledger.playerMismatch': '{name}: проводки выплат {payout|cr} / возвратов {refund|cr}, в расчёте {settledPayout|cr} / {settledRefund|cr}',
  'ledger.commissionMismatch': 'комиссия в проводках {ledger|cr}, в расчёте {settled|cr}',
  'ledger.ok': 'списано {debited|cr} = выплачено {paid|cr} + возвращено {refunded|cr} + банк {bank|cr}',
} as const satisfies Record<string, string>;

export type VerifyCode = keyof typeof VERIFY_MESSAGES_RU;
/** Идентификаторы проверок; заголовок — код title.<id>. */
export type CheckId = 'format' | 'signature' | 'commitment' | 'aggregate' | 'prices' | 'timeline' | 'contributions' | 'settlement' | 'ledger';
export type VerifyParam = string | number | number[] | VerifyNote | VerifyNote[] | { srv: Msg };
/** Строка результата проверки кодом: перевод на клиенте, русский — из VERIFY_MESSAGES_RU. */
export interface VerifyNote {
  code: VerifyCode;
  params?: Record<string, VerifyParam>;
}
/** Словарь сообщений проверки на одном языке: все коды обязательны. */
export type VerifyCatalog = Record<VerifyCode, string>;

export const note = (code: VerifyCode, params?: Record<string, VerifyParam>): VerifyNote => (params ? { code, params } : { code });

/** Форматирование, зависящее от языка. */
export interface VerifyFormat {
  /** шаблон кода на языке показа */
  template: (code: VerifyCode) => string;
  /** сумма в минорных единицах → кредиты с единицей */
  credits: (minor: number) => string;
  /** число с заданным числом знаков после запятой */
  num: (x: number, digits: number) => string;
  /** выбор формы слова для n из форм в порядке категорий языка */
  plural: (n: number, forms: string[]) => string;
  /** сообщение сервера (причина отмены) */
  srv: (m: Msg) => string;
}

const isNote = (v: unknown): v is VerifyNote => typeof v === 'object' && v !== null && 'code' in v;

/** Текст заметки по шаблону языка. Неизвестный параметр оставляется как есть ({x}). */
export function formatNote(n: VerifyNote, f: VerifyFormat): string {
  const one = (v: string | number, cr: boolean, fmt: string | undefined): string =>
    typeof v === 'number' ? (cr ? f.credits(v) : fmt === 'num' ? f.num(v, 2) : String(v)) : v;
  return f.template(n.code).replace(/\{(\w+)(?:\|(cr|num))?(?::([^}]*))?\}/g, (whole, key: string, fmt: string | undefined, forms: string | undefined) => {
    const v = n.params?.[key];
    if (v === undefined) return whole;
    if (forms !== undefined) return f.plural(Number(v), forms.split('|'));
    if (Array.isArray(v)) {
      if (v.length && isNote(v[0])) return (v as VerifyNote[]).map((x) => formatNote(x, f)).join('; ');
      return (v as number[]).map((x) => one(x, fmt === 'cr', fmt)).join(' / ');
    }
    if (typeof v === 'object') return 'srv' in v ? f.srv(v.srv) : formatNote(v, f);
    return one(v, fmt === 'cr', fmt);
  });
}

// ───────────── русский текст (консоль, поля title/details/summary) ─────────────

const numRu = (x: number, digits: number) => x.toFixed(digits).replace('.', ',');
/** Склонение по-русски: формы one|few|many. */
const pluralRu = (n: number, forms: string[]) => {
  const [one, few, many] = [forms[0] ?? '', forms[1] ?? forms[0] ?? '', forms[2] ?? forms[1] ?? forms[0] ?? ''];
  const m10 = n % 10;
  const m100 = n % 100;
  return m10 === 1 && m100 !== 11 ? one : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14) ? few : many;
};

export const FORMAT_RU: VerifyFormat = {
  template: (code) => VERIFY_MESSAGES_RU[code] ?? code,
  credits: (m) => `${numRu(m / 100, 2)} кр.`,
  num: numRu,
  plural: pluralRu,
  srv: msgRu,
};

/** Русский текст заметки. */
export const noteRu = (n: VerifyNote): string => formatNote(n, FORMAT_RU);
